import { injectMutation, injectQuery, injectInfiniteQuery } from '@tanstack/angular-query-experimental';
import { ResponseJson } from '../@core/models/model';

export const createQueryHook = <TData>(
    queryKey: readonly unknown[] | (() => readonly unknown[]),
    queryFn: () => Promise<ResponseJson<TData>>,
    options?: any
) => {
    return injectQuery(() => ({
        queryKey: typeof queryKey === 'function' ? queryKey() : queryKey,
        queryFn: queryFn,
        ...options,
        ...(typeof options?.enabled === 'function' ? {
            enabled: options.enabled()
        } : {})
    }))
}

export const createInfiniteQueryHook = <TData>(
    queryKey: readonly unknown[] | (() => readonly unknown[]),
    queryFn: (context: { pageParam: number }) => Promise<ResponseJson<TData>>,
    options?: any
) => {
    const defaultGetNextPageParam = (lastPage: any, allPages: any[]) => {
        if (lastPage && lastPage.success && lastPage.data) {
            const pageSize = options?.pageSize || 20;
            const currentPage = allPages.length;

            const threads = lastPage.data.threads
            const threadsLength = threads.length;
            const total = lastPage.data.total;

            const totalFetched = allPages.reduce((sum: number, page: any) => {
                const pageThreads = page?.data?.threads ?? []
                return sum + pageThreads.length;
            }, 0);

            const hasMore = total !== undefined
                ? totalFetched < total
                : threadsLength >= pageSize;

            return hasMore ? currentPage + 1 : undefined;
        }
        return undefined;
    };

    return injectInfiniteQuery(() => ({
        queryKey: typeof queryKey === 'function' ? queryKey() : queryKey,
        queryFn: queryFn,
        initialPageParam: 1,
        getNextPageParam: options?.getNextPageParam || defaultGetNextPageParam,
        ...options,
        ...(typeof options?.enabled === 'function' ? {
            enabled: options.enabled()
        } : {})
    }))
}

export const createMutationHook = <TVariables, TData>(
    mutationFn: (variables: TVariables) => Promise<ResponseJson<TData>>,
    onSuccess?: (data: ResponseJson<TData>, variables: TVariables, context: unknown) => void,
    onError?: (error: any, variables: TVariables, context: unknown) => void
) => {
    return injectMutation(() => ({
        mutationFn: mutationFn,
        ...(onSuccess ? { onSuccess: onSuccess } : {}),
        ...(onError ? { onError: onError } : {})
    }))
}
